export class AppError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

// Error categories for grouping and styling
export const ERROR_CATEGORIES = {
  AUTH: 'auth',
  RAFFLE: 'raffle',
  ENTRY: 'entry',
  PAYMENT: 'payment',
  VALIDATION: 'validation',
  NETWORK: 'network',
  SYSTEM: 'system',
};

// Severity levels for styling priority
export const ERROR_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

// Rich error definitions with message, category, severity, and recovery suggestions
const ERROR_DEFINITIONS = {
  // Authentication errors
  AUTH_REQUIRED: {
    message: 'Please log in to continue',
    category: ERROR_CATEGORIES.AUTH,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Sign in to your account to access this feature.',
    action: { label: 'Log In', path: '/auth/login' },
  },
  PHONE_NOT_VERIFIED: {
    message: 'Phone verification required',
    category: ERROR_CATEGORIES.AUTH,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Verify your phone number to buy entries or create competitions.',
    action: { label: 'Verify Phone', path: '/auth/verify-phone' },
  },
  PERMISSION_DENIED: {
    message: "You don't have permission to do this",
    category: ERROR_CATEGORIES.AUTH,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'Contact support if you believe this is a mistake.',
  },

  // Raffle/Competition errors
  RAFFLE_NOT_FOUND: {
    message: 'Competition not found',
    category: ERROR_CATEGORIES.RAFFLE,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'This competition may have been removed or the link is incorrect.',
    action: { label: 'Browse Competitions', path: '/' },
  },
  USER_NOT_FOUND: {
    message: 'User not found',
    category: ERROR_CATEGORIES.AUTH,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'This account may have been deleted.',
  },
  RAFFLE_NOT_ACTIVE: {
    message: 'This competition is not active',
    category: ERROR_CATEGORIES.RAFFLE,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'The competition may be pending, ended, or cancelled.',
    action: { label: 'Browse Active Competitions', path: '/' },
  },
  RAFFLE_ENDED: {
    message: 'This competition has ended',
    category: ERROR_CATEGORIES.RAFFLE,
    severity: ERROR_SEVERITY.INFO,
    suggestion: 'Check if there\'s a winner announced or browse other competitions.',
    action: { label: 'Browse Competitions', path: '/' },
  },

  // Entry purchase errors
  RAFFLE_SOLD_OUT: {
    message: 'All entries sold out',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.INFO,
    suggestion: 'This competition has reached maximum entries. Try another competition.',
    action: { label: 'Browse Competitions', path: '/' },
  },
  INSUFFICIENT_TICKETS: {
    message: 'Not enough entries available',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Try selecting fewer entries.',
  },
  MAX_TICKETS_REACHED: {
    message: 'Maximum entries reached',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.INFO,
    suggestion: 'You\'ve bought the maximum allowed entries for this competition.',
  },
  EXCEEDS_TICKET_LIMIT: {
    message: 'Entry limit exceeded',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Select fewer entries to stay within your allowance.',
  },
  RATE_LIMIT_EXCEEDED: {
    message: 'Too many purchases',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Please wait a few minutes before trying again.',
  },
  NEW_USER_SPEND_LIMIT: {
    message: 'New account spending limit',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.INFO,
    suggestion: 'New accounts have a €50 limit for the first 7 days to protect you.',
  },
  SKILL_ANSWER_WRONG: {
    message: 'Incorrect answer',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Read the question carefully and try again.',
  },
  CANNOT_ENTER_OWN_RAFFLE: {
    message: 'Cannot enter your own competition',
    category: ERROR_CATEGORIES.ENTRY,
    severity: ERROR_SEVERITY.INFO,
    suggestion: 'You cannot buy entries in competitions you created.',
  },

  // Seller/Payment errors
  SELLER_NOT_ONBOARDED: {
    message: 'Payment setup incomplete',
    category: ERROR_CATEGORIES.PAYMENT,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Set up your payment account to receive funds.',
    action: { label: 'Set Up Payments', path: '/settings/payments' },
  },
  ALREADY_HAS_ACTIVE_RAFFLE: {
    message: 'Active competition limit reached',
    category: ERROR_CATEGORIES.RAFFLE,
    severity: ERROR_SEVERITY.INFO,
    suggestion: 'You can only have one active competition at a time.',
    action: { label: 'View My Competitions', path: '/dashboard' },
  },
  STRIPE_ERROR: {
    message: 'Payment processing failed',
    category: ERROR_CATEGORIES.PAYMENT,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'Please check your payment details and try again.',
  },

  // Validation errors
  INVALID_TICKET_COUNT: {
    message: 'Invalid entry quantity',
    category: ERROR_CATEGORIES.VALIDATION,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Select between 1 and 10 entries.',
  },
  INVALID_END_DATE: {
    message: 'Invalid end date',
    category: ERROR_CATEGORIES.VALIDATION,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'End date must be 1-30 days from now.',
  },
  MIN_PHOTOS_REQUIRED: {
    message: 'Photos required',
    category: ERROR_CATEGORIES.VALIDATION,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Upload at least 1 photo of your item.',
  },
  MAX_PHOTOS_EXCEEDED: {
    message: 'Too many photos',
    category: ERROR_CATEGORIES.VALIDATION,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Remove some photos. Maximum 8 allowed.',
  },

  // Account deletion errors
  CONFIRMATION_REQUIRED: {
    message: 'Confirmation required',
    category: ERROR_CATEGORIES.VALIDATION,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Type "DELETE MY ACCOUNT" to confirm deletion.',
  },
  DELETION_FAILED: {
    message: 'Account deletion failed',
    category: ERROR_CATEGORIES.SYSTEM,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'Please try again. If the problem persists, contact support.',
  },

  // System errors
  INTERNAL_ERROR: {
    message: 'Something went wrong',
    category: ERROR_CATEGORIES.SYSTEM,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'Please try again. If the problem persists, contact support.',
  },
  NETWORK_ERROR: {
    message: 'Connection problem',
    category: ERROR_CATEGORIES.NETWORK,
    severity: ERROR_SEVERITY.WARNING,
    suggestion: 'Check your internet connection and try again.',
  },
};

// Legacy flat message map for backwards compatibility
const ERROR_MESSAGES = Object.fromEntries(
  Object.entries(ERROR_DEFINITIONS).map(([code, def]) => [code, def.message])
);

/**
 * Get user-friendly error message from error object
 */
export function getErrorMessage(error) {
  const code = error?.code || error?.errorCode || 'INTERNAL_ERROR';
  return ERROR_MESSAGES[code] || 'Something went wrong. Please try again.';
}

/**
 * Get full error definition with message, category, severity, and suggestions
 */
export function getErrorDefinition(error) {
  const code = error?.code || error?.errorCode || error || 'INTERNAL_ERROR';
  const codeStr = typeof code === 'string' ? code : 'INTERNAL_ERROR';

  // Check for network errors first
  if (isNetworkError(error)) {
    return ERROR_DEFINITIONS.NETWORK_ERROR;
  }

  return ERROR_DEFINITIONS[codeStr] || {
    message: error?.message || 'Something went wrong',
    category: ERROR_CATEGORIES.SYSTEM,
    severity: ERROR_SEVERITY.ERROR,
    suggestion: 'Please try again. If the problem persists, contact support.',
  };
}

/**
 * Get icon name for error category
 */
export function getErrorIcon(category) {
  const icons = {
    [ERROR_CATEGORIES.AUTH]: 'lock',
    [ERROR_CATEGORIES.RAFFLE]: 'ticket',
    [ERROR_CATEGORIES.ENTRY]: 'shopping-cart',
    [ERROR_CATEGORIES.PAYMENT]: 'credit-card',
    [ERROR_CATEGORIES.VALIDATION]: 'alert-circle',
    [ERROR_CATEGORIES.NETWORK]: 'wifi-off',
    [ERROR_CATEGORIES.SYSTEM]: 'alert-triangle',
  };
  return icons[category] || 'alert-circle';
}

/**
 * Get color class for error severity
 */
export function getErrorColors(severity) {
  const colors = {
    [ERROR_SEVERITY.INFO]: {
      bg: 'bg-primary/10',
      border: 'border-primary/30',
      text: 'text-primary',
      icon: 'text-primary',
    },
    [ERROR_SEVERITY.WARNING]: {
      bg: 'bg-warning/10',
      border: 'border-warning/30',
      text: 'text-warning',
      icon: 'text-warning',
    },
    [ERROR_SEVERITY.ERROR]: {
      bg: 'bg-error/10',
      border: 'border-error/30',
      text: 'text-error',
      icon: 'text-error',
    },
  };
  return colors[severity] || colors[ERROR_SEVERITY.ERROR];
}

export function isAuthError(error) {
  const code = error?.code || '';
  return code.startsWith('auth/') || code === 'AUTH_REQUIRED' || code === 'PHONE_NOT_VERIFIED';
}

export function isNetworkError(error) {
  return (
    error?.code === 'unavailable' ||
    error?.code === 'NETWORK_ERROR' ||
    error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('offline') ||
    error?.message?.toLowerCase().includes('connection')
  );
}

/**
 * Check if error is recoverable (user can retry or take action)
 */
export function isRecoverableError(error) {
  const def = getErrorDefinition(error);
  return def.severity !== ERROR_SEVERITY.ERROR || !!def.action;
}
